import { uploadFileToS3 } from './s3MultipartUploader'

const sleep = ms => new Promise(r => setTimeout(r, ms))

/**
 * Upload a set of tile File objects to S3 concurrently, each via a single presigned PUT.
 * @param {Object} p
 * @param {File[]} p.tiles - File objects (each has .name, .size)
 * @param {Record<string,{put_url:string}>} p.presignedByName - presigned PUT per tile filename
 * @param {number} [p.concurrency=5]
 * @param {number} [p.retries=2] - extra attempts per tile after the first
 * @param {(pct:number)=>void} [p.onProgress] - aggregate 0..100 across all tiles
 * @param {(filename:string, err:Error)=>void} [p.onTileError]
 * @param {AbortSignal} [p.signal]
 * @returns {Promise<{ done: string[], failed: string[] }>}
 */
export async function uploadTilesConcurrently({
  tiles,
  presignedByName,
  concurrency = 5,
  retries = 2,
  onProgress,
  onTileError,
  signal,
}) {
  const totalBytes = tiles.reduce((s, t) => s + (t.size || 0), 0) || 1
  const bytesDone = new Map() // filename -> bytes
  const done = []
  const failed = []

  function emit() {
    if (!onProgress) return
    let sum = 0
    for (const v of bytesDone.values()) sum += v
    onProgress(Math.min(100, Math.round((sum / totalBytes) * 100)))
  }

  async function uploadOne(file) {
    const presigned = presignedByName[file.name]
    if (!presigned?.put_url) {
      failed.push(file.name)
      return
    }
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        await uploadFileToS3({
          file,
          initiateResponse: { mode: 'single', put_url: presigned.put_url },
          signal,
          onProgress: pct => {
            bytesDone.set(file.name, (file.size || 0) * (pct / 100))
            emit()
          },
        })
        bytesDone.set(file.name, file.size || 0)
        emit()
        done.push(file.name)
        return
      } catch (err) {
        if (signal?.aborted) {
          failed.push(file.name)
          return
        }
        if (attempt === retries) {
          failed.push(file.name)
          onTileError?.(file.name, err)
          return
        }
        await sleep(1000 * 2 ** attempt)
      }
    }
  }

  const queue = [...tiles]
  async function worker() {
    while (queue.length) {
      if (signal?.aborted) return
      const file = queue.shift()
      if (!file) return
      await uploadOne(file)
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(concurrency, tiles.length) }, () =>
      worker(),
    ),
  )
  return { done, failed }
}
